import { createContext, useCallback, useEffect, useId, useMemo, useState } from "react";
import type { KeyboardEvent, SyntheticEvent } from "react";
import { styled } from "@mui/material";
import MuiTooltip, { tooltipClasses } from "@mui/material/Tooltip";
import type { TooltipProps as MuiTooltipProps } from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";

const enterDelay = 250;
const leaveDelay = 100;

/** Distance between the anchor and the bubble, matching the tooltips elsewhere in the app. */
const anchorOffset = 7;

export interface OnboardingTooltipContextProps {
  /**
   * @description Called when the user dismisses the tooltip,
   * clicking on the close button inside the tooltip.
   */
  onClose?: (event: SyntheticEvent | Event) => void;
  /** @description Id of the element naming the bubble, applied by `<OnboardingTooltipTitle />`.*/
  titleId: string;
  /** @description Id of the element describing the bubble, applied by `<OnboardingTooltipDescription />`.*/
  descriptionId: string;
  /**
   * @description Name of the feature being introduced, used to spell out where the parts of the
   * bubble lead for anyone who cannot see which control it points at.
   */
  feature: string;
}

export const OnboardingTooltipContext = createContext<OnboardingTooltipContextProps | undefined>(undefined);
OnboardingTooltipContext.displayName = "OnboardingTooltipContext";

/**
 * @description Announces the bubble to screen readers when it opens.
 */
const OnboardingTooltipAnnouncement = styled("div")({
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
});

const OnboardingTooltipRoot = styled(({ className, ...props }: MuiTooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  width: "340px",

  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary1["10"],
    border: `2px solid ${theme.palette.secondary1["100"]}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.body1.fontWeight,
  },

  [`& .${tooltipClasses.arrow}::before`]: {
    backgroundColor: theme.palette.secondary1["10"],
    border: `2px solid ${theme.palette.secondary1["100"]}`,
  },
}));

export interface OnboardingTooltipProps extends MuiTooltipProps {
  /**
   * @description Name of the feature being introduced. Announced when the bubble
   * opens and used to say where `<OnboardingTooltipLink />` leads.
   *
   * @example "Circuits".
   */
  feature: string;
}

/** @description The parts of the Popper instance needed to keep the bubble on its anchor. */
interface PopperHandle {
  update: () => void;
  state: { elements: { reference: unknown } };
}

/**
 * @description A bubble that introduces a new feature to the user, pointing at the control that
 * opens it.
 */
function OnboardingTooltip(props: OnboardingTooltipProps) {
  const { children, title, open = false, onClose, placement = "bottom", feature, ...rest } = props;

  const { t } = useTranslation();

  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  /**
   * Escape only counts while the key comes from inside the bubble. It avoids listening
   * for Escape key pressed anywhere in the app, which would be unexpected behavior.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Escape") return;
      onClose?.(event);
    },
    [onClose]
  );

  const context = useMemo(
    () => ({ onClose, titleId, descriptionId, feature }),
    [onClose, titleId, descriptionId, feature]
  );

  const [announcement, setAnnouncement] = useState("");

  /**
   * Filled after the region has mounted empty, because a live region only announces changes made
   * to it once it is already on the page.
   */
  useEffect(() => {
    if (!open) {
      setAnnouncement("");
      return;
    }
    setAnnouncement(t("global.onboarding_announcement", { feature }));
  }, [open, feature, t]);

  const [popper, setPopper] = useState<PopperHandle | null>(null);

  /**
   * Popper measures the anchor when the bubble opens and afterwards only listens for scrolls and
   * window resizes.
   */
  useEffect(() => {
    const anchor = popper?.state.elements.reference;
    if (!open || !popper || !(anchor instanceof Element)) return undefined;

    const observer = new ResizeObserver(() => popper.update());
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [open, popper]);

  const memoSlotProps = useMemo<MuiTooltipProps["slotProps"]>(
    () => ({
      ...rest.slotProps,
      popper: {
        ...rest.slotProps?.popper,
        popperRef: setPopper,
        popperOptions: { modifiers: [{ name: "offset", options: { offset: [0, anchorOffset] } }] },
      },
      /** The bubble holds interactive content, so it is a dialog rather than a tooltip. */
      tooltip: {
        ...rest.slotProps?.tooltip,
        role: "dialog",
        "aria-labelledby": titleId,
        "aria-describedby": descriptionId,
        onKeyDown: handleKeyDown,
      },
      transition: { timeout: { enter: enterDelay, exit: leaveDelay } },
    }),
    [rest.slotProps, titleId, descriptionId, handleKeyDown]
  );

  return (
    <ThemeProvider2024>
      <OnboardingTooltipContext.Provider value={context}>
        <OnboardingTooltipRoot
          {...rest}
          /**
           * aria-labelledby is set to undefined because the tooltip is a dialog,
           * and the title is already referenced by aria-labelledby on the tooltip itself.
           */
          aria-labelledby={undefined}
          title={title}
          open={open}
          placement={placement}
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          slotProps={memoSlotProps}
        >
          {children}
        </OnboardingTooltipRoot>
        <OnboardingTooltipAnnouncement role="status">{announcement}</OnboardingTooltipAnnouncement>
      </OnboardingTooltipContext.Provider>
    </ThemeProvider2024>
  );
}

export default OnboardingTooltip;
